import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import Spacer from '@berlitz/spacer'
import { getMessages } from '../constants'
import Button from '@components/Button'
import Modal from '@berlitzplatforms/micro.ui.modal'
import FormModal from './Modal'
import { AddDay, DateInput, DayInput, DeleteDay, FindButton, TimeInput } from './Fields'
import { useFMIBulkForm } from './useFMIBulkForm'
import { OrderedListNumber, StepBox, StepLabel } from '../style'
import {
  BulkFormField,
  BulkFormWrapper,
  FormBox,
  FormDateBox,
  FormDayTimeAddBox,
  FormDayTimeAddSearchBox,
  FormModalBox,
  SearchWrapper,
} from './style'

const Form: React.FC = () => {
  const intl = useIntl()
  const messages = getMessages()
  const modalRef = useRef<HTMLDivElement>(null)

  const [openModalForm, setOpenModalForm] = useState(false)
  const {
    values,
    dayPlaceholders,
    isDateDirty,
    maxDate,
    isLastDayTimeItem,
    isAddButtonDisabled,
    isFindButtonDisabled,
    onDateChange,
    onDayChange,
    onTimeChange,
    onAddClick,
    onDeleteClick,
    onFindClick,
  } = useFMIBulkForm()

  const handleResize = useCallback(() => {
    const element = modalRef.current
    const computedStyle = window.getComputedStyle(element as Element)
    const newIsHidden = computedStyle.display === 'none'
    openModalForm && newIsHidden && setOpenModalForm(false)
  }, [openModalForm])

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [handleResize])

  return (
    <BulkFormWrapper>
      <StepBox>
        <StepBox>
          <OrderedListNumber>1</OrderedListNumber>
          <StepLabel>{intl.formatMessage(messages.step1Label)}</StepLabel>
        </StepBox>
      </StepBox>

      <Spacer size="xs" />

      <FormModalBox ref={modalRef}>
        <Button
          onClick={() => {
            setOpenModalForm(true)
          }}
          fullWidth
        >
          {intl.formatMessage(messages.formModalButton)}
        </Button>

        <Modal open={openModalForm} onClose={() => setOpenModalForm(false)}>
          <FormModal onClose={() => setOpenModalForm(false)} />
        </Modal>

        <Spacer size="xs" />
      </FormModalBox>

      <FormBox>
        <FormDateBox>
          <BulkFormField>
            <label>{intl.formatMessage(messages.formDate)}</label>
            <div>
              <DateInput
                value={values.PreferredStartDate}
                isDirty={isDateDirty}
                maxDate={maxDate}
                onChange={onDateChange}
              />
            </div>
          </BulkFormField>
        </FormDateBox>

        {values.Day.map((Day, index) => {
          const isLastItem = isLastDayTimeItem(index)

          return (
            <FormDayTimeAddSearchBox key={`Day-${index}`} style={{ flexGrow: isLastItem ? 1 : 0 }}>
              <FormDayTimeAddBox>
                <BulkFormField>
                  <label>{intl.formatMessage(messages.formDay)}</label>
                  <DayInput value={dayPlaceholders[index]} onChange={val => onDayChange(val, index)} />

                  <label>{intl.formatMessage(messages.formTime)}</label>
                  <TimeInput value={Day.StartTime} onChange={val => onTimeChange(val, index)} />

                  {values.Day.length > 1 ? <DeleteDay onClick={() => onDeleteClick(index)} /> : null}
                </BulkFormField>

                {isLastItem ? <AddDay onClick={onAddClick} disabled={isAddButtonDisabled()} /> : null}
              </FormDayTimeAddBox>

              {isLastItem ? (
                <SearchWrapper>
                  <FindButton onClick={onFindClick} disabled={isFindButtonDisabled()} />
                </SearchWrapper>
              ) : null}
            </FormDayTimeAddSearchBox>
          )
        })}
      </FormBox>
    </BulkFormWrapper>
  )
}

export default Form
